export type SkillPracticeTaskStatus = 'CREATED' | 'IN_WORK' | 'COMPLETED' | 'CANCELLED';
import { TranslationBundle } from './translation-bundle.model';
import { LearningGradeSettings } from 'app/models/general-setting.model';

export type SkillPracticeTaskType = 'PRACTICE' | 'TEST_REVIEW';
export const SKILL_LEVEL_TYPE_TEST = 'TEST';
export const SKILL_LEVEL_TYPE_TASK = 'TASK';
export const SKILL_REQUIREMENT_COMPANY = 'COMPANY_PROBATION';
export const SKILL_REQUIREMENT_DEPARTMENT = 'DEPARTMENT_PROBATION';
export const SKILL_REQUIREMENT_FUTURE_HEAD = 'FUTURE_HEAD';
export const SKILL_REQUIREMENT_CURRENT_HEAD = 'CURRENT_HEAD';
export const SKILL_ERROR_STRUCTURE_INVALID = 'SKILL_STRUCTURE_INVALID';
export const SKILL_ERROR_CONFIGURATION_CONFLICT = 'SKILL_CONFIGURATION_CONFLICT';
export const SKILL_ERROR_STRUCTURE_REQUIRED = 'SKILL_STRUCTURE_REQUIRED_LEVEL';
export const SKILL_ERROR_STRUCTURE_ACTIVE_TASK = 'SKILL_STRUCTURE_ACTIVE_TASK';
export const SKILL_ERROR_REQUIREMENT_UNPUBLISHED = 'SKILL_REQUIREMENT_UNPUBLISHED';
export const SKILL_ERROR_DEACTIVATION_REQUIREMENT_ACTIVE = 'SKILL_DEACTIVATION_REQUIREMENT_ACTIVE';
export const SKILL_ERROR_DEACTIVATION_ACTIVE_TASK = 'SKILL_DEACTIVATION_ACTIVE_TASK';
export const SKILL_ERROR_LEVEL_NOT_AVAILABLE = 'LEVEL_NOT_AVAILABLE';
export const SKILL_TEST_STATE_IDLE = 'idle';
export const SKILL_TEST_STATE_GENERATING = 'generating';
export const SKILL_TEST_STATE_TESTING = 'testing';
export const SKILL_TEST_STATE_RESULT = 'result';
export const SKILL_TEST_STATE_ERROR = 'error';

export type SkillLevelType = typeof SKILL_LEVEL_TYPE_TEST | typeof SKILL_LEVEL_TYPE_TASK;
export type SkillTrainingRequirementScope =
  typeof SKILL_REQUIREMENT_COMPANY
  | typeof SKILL_REQUIREMENT_DEPARTMENT
  | typeof SKILL_REQUIREMENT_FUTURE_HEAD
  | typeof SKILL_REQUIREMENT_CURRENT_HEAD;
export type SkillConfigurationErrorCode =
  typeof SKILL_ERROR_STRUCTURE_INVALID
  | typeof SKILL_ERROR_CONFIGURATION_CONFLICT
  | typeof SKILL_ERROR_STRUCTURE_REQUIRED
  | typeof SKILL_ERROR_STRUCTURE_ACTIVE_TASK
  | typeof SKILL_ERROR_DEACTIVATION_REQUIREMENT_ACTIVE
  | typeof SKILL_ERROR_DEACTIVATION_ACTIVE_TASK
  | typeof SKILL_ERROR_REQUIREMENT_UNPUBLISHED
  | typeof SKILL_ERROR_LEVEL_NOT_AVAILABLE;
export type SkillSpecializationStatus = 'DRAFT' | 'FILLING_IN_PROGRESS' | 'READY_FOR_LEARNING';
export type SkillSpecializationFolderId = 'OWN' | 'OTHER' | 'CROSS' | 'COMPANY';
export type SkillSpecializationFillTaskStatus =
  'CREATING'
  | 'CREATED'
  | 'IN_WORK'
  | 'READY_FOR_REVIEW'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'COMPLETED_INVALID';
export type AiChatRole = 'user' | 'assistant';
export type UserRole = 'ROLE_HEAD' | 'ROLE_ADMIN' | 'ROLE_ADMIN_SPECIALIZATION' | 'ROLE_USER';
export type SkillAiOperation = 'CHAT' | 'TEST_GENERATION' | 'STANDARD_TEST_GENERATION';
export type StandardAiQuestionLanguage = 'RUSSIAN' | 'ENGLISH';
export type StandardAiQuestionGenerationStatusName = 'PENDING' | 'PROCESSING' | 'SUCCEEDED'
  | 'FAILED' | 'MANUAL_OVERRIDE' | 'SKIPPED_NO_SOURCE';

export {
  SkillDepartmentStarMapCellResponse,
  SkillDepartmentStarMapEmployeeResponse,
  SkillDepartmentStarMapResponse,
  SkillDepartmentStarMapSpecializationResponse,
  SkillDepartmentStarMapTotalsResponse,
} from '../options/department-star-map.option';

export interface SkillSpecializationCreateRequest {
  name: string;
  description: string;
  groupId?: string;
  fillAssigneeId?: string;
  curators?: SkillCuratorAssignmentRequest[];
  curatorIds?: string[];
  excludedFromLearningControl?: boolean;
  crossDepartmental?: boolean;
  participantGroupIds?: string[];
  responsibleGroupByDepartmentId?: SkillResponsibleGroupByDepartmentId;
  expectedRevision?: number;
  configuredLevelCount?: number;
  level2Type?: SkillLevelType;
  level3Type?: SkillLevelType;
}

export interface SkillLevelStructureUpdateRequest {
  configuredLevelCount: number;
  level2Type?: SkillLevelType;
  level3Type?: SkillLevelType;
  expectedRevision: number;
  departmentId?: string;
}

export interface SkillTrainingRequirementUpdateRequest {
  requiredLevel: number;
  expectedRevision: number;
  departmentId?: string;
  responseDepartmentId?: string;
}

export interface SkillTrainingRequirementDeleteParams {
  expectedRevision: number;
  departmentId?: string;
  responseDepartmentId?: string;
}

export interface SkillSpecializationDeactivateParams {
  expectedRevision: number;
  departmentId?: string;
}

export interface SkillRequirementUpdateCommand {
  scope: SkillTrainingRequirementScope;
  request: SkillTrainingRequirementUpdateRequest;
}

export interface SkillRequirementDeleteCommand {
  scope: SkillTrainingRequirementScope;
  params: SkillTrainingRequirementDeleteParams;
}

export interface SkillUploadDocumentCommand {
  level: number;
  file: File;
  expectedRevision: number;
}

export interface SkillLearningErrorResponse {
  code: string;
  message: string;
  limit?: number;
  retryAfterSeconds?: number;
}

export type SkillTestViewState =
  typeof SKILL_TEST_STATE_IDLE
  | typeof SKILL_TEST_STATE_GENERATING
  | typeof SKILL_TEST_STATE_TESTING
  | typeof SKILL_TEST_STATE_RESULT
  | typeof SKILL_TEST_STATE_ERROR;

export interface SkillLearningHttpError {
  status: number;
  json?: () => SkillLearningErrorResponse;
  text?: () => string;
  _body?: string;
}

export interface SkillTestQuestionViewModel {
  originalIndex: number;
  questionText: string;
  questionTextTranslation?: TranslationBundle | null;
  answers: string[];
  answerTranslations?: (TranslationBundle | null)[];
}

export interface SkillAdminHelpRow {
  titleKey: string;
  textKey: string;
}

export interface SkillSpecializationCreationPermissions {
  canRequireProbationCompletion: boolean;
  canCreateCrossDepartmental: boolean;
}

export interface SkillCuratorAssignmentRequest {
  userId: string;
  maxAcceptedLevel: number;
}

export interface SkillResponsibleGroupByDepartmentId {
  [departmentId: string]: string;
}

export interface EditSpecializationMessageParams {
  level?: number;
}

export interface SpecializationFormValue {
  name: string;
  description: string;
  groupId: string;
  fillAssigneeId: string;
  curatorIds: string[];
  curators: SkillCuratorAssignmentRequest[];
  crossDepartmental: boolean;
  participantGroupIds: string[];
  responsibleGroupByDepartmentId: SkillResponsibleGroupByDepartmentId;
  responsibleGroupId: string;
}

export interface SpecializationFormInitialValue {
  name: string;
  description: string;
  groupId: string;
  curatorIds: string[];
  curators: SkillCuratorAssignmentResponse[];
  fillAssigneeId: string;
  crossDepartmental: boolean;
  participantGroupIds: string[];
  responsibleGroupByDepartmentId: SkillResponsibleGroupByDepartmentId;
  responsibleGroupId: string;
}

export interface SpecializationFormData {
  manageableCuratorGroupIds: string[];
  availableGroups: SkillGroup[];
  availableCurators: SkillAvailableCurator[];
  availableFillAssignees: SkillAvailableCurator[];
}

export interface SpecializationFormOptions {
  showGroupSelector: boolean;
  showCurators: boolean;
  showFillAssigneeSelector: boolean;
  showCrossDepartmentalOption: boolean;
  showCrossDepartmentCurators: boolean;
  responsibleGroupsLocked: boolean;
  requiredResponsibleGroupId?: string;
  isSubmitting: boolean;
  submitLabel: string;
  requireCurator: boolean;
  requireFillAssignee: boolean;
  maxCurators: number;
  showFillAssigneeHint: boolean;
  curatorLabelKey: string;
  canAssignAnyCuratorLevel: boolean;
  configuredLevelCount: number;
  contentEditable: boolean;
}

export interface SpecializationCuratorSelectionValue {
  responsibleGroupId: string;
  curatorIds: string[];
  curators: SkillCuratorAssignmentRequest[];
}

export interface CuratorAssignRequest {
  curatorIds?: string[];
  curators?: SkillCuratorAssignmentRequest[];
  responsibleGroupId?: string;
}

export type SkillCuratorAssignmentInput =
  CuratorAssignRequest
  | SkillCuratorAssignmentRequest[];

export interface TheoryLevelRequest {
  description: string;
  topics?: string[];
  questionsCount: number;
  timeLimitMinutes: number;
  passingPercentage: number;
  testQuestions?: SkillTestQuestion[];
  expectedRevision?: number;
}

export interface TheoryTestGenerationRequest {
  description: string;
}

export interface PracticeLevelRequest {
  description: string;
  expectedRevision?: number;
}

export interface AiChatSendRequest {
  message: string;
}

export interface SkillTestSubmitRequest {
  submissionId?: string;
  answers: { [questionIndex: string]: number };
}

export interface SkillLearningGeneralSettingsResponse {
  testRetakeCooldownHours: number;
  testGenerationLimitPerUserPerDay: number;
  maxChatMessageLength: number;
  chatMessagesPerUserPerDay: number;
  chatMessagesPerUserPerMinute: number;
  learningControlPeriodMonths: number;
}

export interface SkillPromptTemplateResponse {
  code: string;
  version: number;
  text: string;
}

export interface TheoryLevelResponse {
  description: string;
  descriptionTranslation?: TranslationBundle | null;
  topics: string[];
  topicTranslations?: (TranslationBundle | null)[];
  documentFileId: string | null;
  document: SkillDocumentResponse | null;
  questionsCount: number;
  timeLimitMinutes: number;
  passingPercentage: number;
  testQuestions?: SkillTestQuestion[];
}

export interface PracticeLevelResponse {
  description: string;
  descriptionTranslation?: TranslationBundle | null;
  documentFileId: string | null;
  document: SkillDocumentResponse | null;
}

export interface SkillSpecializationResponse {
  id: string;
  name: string;
  nameTranslation?: TranslationBundle | null;
  description: string;
  descriptionTranslation?: TranslationBundle | null;
  groupId: string;
  fillAssigneeId: string | null;
  curatorIds: string[];
  curators: SkillCuratorAssignmentResponse[];
  excludedFromLearningControl: boolean;
  status: SkillSpecializationStatus;
  lastFillTask: SkillSpecializationFillTaskResponse | null;
  lastFillValidationError: string | null;
  canCreateFillTask: boolean;
  theoryLevel: TheoryLevelResponse | null;
  theoryLevel2?: TheoryLevelResponse | null;
  theoryLevel3?: TheoryLevelResponse | null;
  practiceLevel2: PracticeLevelResponse | null;
  practiceLevel3: PracticeLevelResponse | null;
  active: boolean;
  requiredForProbationCompletion: boolean;
  allowPartialLevels: boolean;
  publishedLevelCount: number;
  publishedLevel2Type?: SkillLevelType | null;
  publishedLevel3Type?: SkillLevelType | null;
  configuredLevelCount?: number;
  configuredLevel2Type?: SkillLevelType | null;
  configuredLevel3Type?: SkillLevelType | null;
  configurationRevision?: number;
  companyProbationRequiredLevel?: number | null;
  departmentProbationRequiredLevel?: number | null;
  futureHeadRequiredLevel?: number | null;
  currentHeadOnly?: boolean;
  currentHeadRequiredLevel?: number | null;
  canManageCurrentHeadRequirement?: boolean;
  effectiveProbationRequiredLevel?: number | null;
  probationRequiredByCompany?: boolean;
  probationRequiredByDepartment?: boolean;
  probationRequirementSourceDepartmentIds?: string[];
  inheritedFromDepartmentId?: string | null;
  crossDepartmental: boolean;
  participantGroupIds: string[];
  responsibleGroupByDepartmentId: SkillResponsibleGroupByDepartmentId;
  canEditContent: boolean;
  canManageCurators: boolean;
  canChangeProbationRequirement: boolean;
  curatorGroupId: string | null;
  manageableCuratorGroupIds: string[];
  canManageParticipantGroups: boolean;
  owningDepartmentId?: string;
  canChangeLevelStructure?: boolean;
  canManageCompanyProbationRequirement?: boolean;
  canDeleteCompanyProbationRequirement?: boolean;
  canManageDepartmentProbationRequirement?: boolean;
  canManageFutureHeadRequirement?: boolean;
  allowedStructureDepartmentIds?: string[];
}

export interface SkillSpecializationListItem {
  id: string;
  name: string;
  nameTranslation?: TranslationBundle | null;
  description: string;
  descriptionTranslation?: TranslationBundle | null;
  groupId: string;
  groupName?: string | null;
  groupNameTranslation?: TranslationBundle | null;
  fillAssigneeId: string | null;
  curatorIds: string[];
  curators: SkillCuratorAssignmentResponse[];
  excludedFromLearningControl: boolean;
  status: SkillSpecializationStatus;
  lastFillTask: SkillSpecializationFillTaskResponse | null;
  lastFillValidationError: string | null;
  canCreateFillTask: boolean;
  active: boolean;
  requiredForProbationCompletion: boolean;
  publishedLevelCount: number;
  publishedLevel2Type?: SkillLevelType | null;
  publishedLevel3Type?: SkillLevelType | null;
  companyProbationRequiredLevel?: number | null;
  futureHeadRequiredLevel?: number | null;
  currentHeadOnly?: boolean;
  currentHeadRequiredLevel?: number | null;
  canManageCurrentHeadRequirement?: boolean;
  effectiveProbationRequiredLevel?: number | null;
  probationRequiredByCompany?: boolean;
  probationRequiredByDepartment?: boolean;
  probationRequirementSourceDepartmentIds?: string[];
  inheritedFromDepartmentId?: string | null;
  configurationRevision?: number;
  crossDepartmental: boolean;
  canEditContent: boolean;
  canManageCurators: boolean;
  canChangeProbationRequirement: boolean;
  canManageParticipantGroups: boolean;
  canChangeLevelStructure?: boolean;
  canManageCompanyProbationRequirement?: boolean;
  canDeleteCompanyProbationRequirement?: boolean;
  canManageDepartmentProbationRequirement?: boolean;
  canManageFutureHeadRequirement?: boolean;
  canDeactivate?: boolean;
}

export interface SkillSpecializationSourceGroup {
  groupId: string;
  groupName?: string | null;
  groupNameTranslation?: TranslationBundle | null;
  specializations: SkillSpecializationListItem[];
}

export interface SkillSpecializationFillTaskResponse {
  id: string;
  specializationId: string;
  headId: string;
  fillAssigneeId: string | null;
  workerIds: string[];
  curatorId: string | null;
  status: SkillSpecializationFillTaskStatus;
  planfixInternalTaskId: number | null;
  planfixTaskNumber: number | null;
  planfixTaskId: number | null;
  planfixTaskUrl: string | null;
  createdAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
}

export interface SkillProgressResponse {
  id: string;
  userId: string;
  specializationId: string;
  currentLevel: number;
  topicChecklist: { [topicIndex: string]: boolean };
  testPassedAt: string | null;
  level1SelfTestPassedAt: string | null;
  level2TestReviewPassedAt: string | null;
  level1SelfTestPassed: boolean;
  level1Task: SkillPracticeTaskResponse | null;
  canCreateLevel1Task: boolean;
  level1TaskCooldownUntil: string | null;
  level1TaskStatus: SkillPracticeTaskStatus | null;
  level2TaskStatus: SkillPracticeTaskStatus | null;
  level3TaskStatus: SkillPracticeTaskStatus | null;
  level2TestReviewTask: SkillPracticeTaskResponse | null;
  level2TestReviewTaskStatus: SkillPracticeTaskStatus | null;
  level2TestReviewTaskCooldownUntil: string | null;
  canCreateLevel2TestReviewTask: boolean;
  level2PracticeTask: SkillPracticeTaskResponse | null;
  level2PracticeTaskStatus: SkillPracticeTaskStatus | null;
  level2PracticeTaskCooldownUntil: string | null;
  level3TaskCooldownUntil: string | null;
  canCreateLevel2PracticeTask: boolean;
  canRetakeTest: boolean;
}

export interface SkillTrainingProgressResponse {
  specializationId: string;
  specializationName: string;
  specializationNameTranslation?: TranslationBundle | null;
  completedLevels: number;
  totalLevels: number;
  completed: boolean;
}

export interface LearningTreeSpecialization {
  id: string;
  name: string;
  nameTranslation?: TranslationBundle | null;
  description: string;
  descriptionTranslation?: TranslationBundle | null;
  completedLevels: number;
  totalLevels: number;
  completed: boolean;
  effectiveProbationRequiredLevel?: number | null;
  probationRequiredByCompany?: boolean;
  probationRequiredByDepartment?: boolean;
  probationRequirementSourceDepartmentIds?: string[];
  inheritedFromDepartmentId?: string | null;
  futureHeadRequiredLevel?: number | null;
  currentHeadOnly?: boolean;
  currentHeadRequiredLevel?: number | null;
  canManageCurrentHeadRequirement?: boolean;
}

export interface GroupedSpecialization {
  id: string;
  name: string;
  nameTranslation?: TranslationBundle | null;
  departmentScope?: 'DEPARTMENT' | 'SUBDEPARTMENT' | null;
  group: {
    id: string;
    name: string;
    nameTranslation?: TranslationBundle | null;
  };
  completedTrainings: number;
  totalTrainings: number;
  specializations: LearningTreeSpecialization[];
}

export interface LearningHelpRow {
  titleKey: string;
  textKey: string;
}

export interface SkillDepartmentProgressResponse {
  groupId: string;
  groupName: string;
  groupNameTranslation?: TranslationBundle | null;
  departmentScope?: 'DEPARTMENT' | 'SUBDEPARTMENT' | null;
  completedTrainings: number;
  totalTrainings: number;
  trainings: SkillTrainingProgressResponse[];
}

export interface SkillDepartmentLearningProgressResponse {
  totalCompletedTrainings: number;
  totalTrainings: number;
  departments: SkillDepartmentProgressResponse[];
}

export interface SkillWeightSumResponse {
  sumLevel: number;
  sumWeight: number;
}

export type SkillScoreStatus = 'OK' | 'DEADLINE_SOON' | 'PENALTY_ZONE';

export interface SkillScoreHistoryItemResponse {
  specializationId: string;
  specializationName: string;
  specializationNameTranslation?: TranslationBundle | null;
  level: number;
  points: number;
  completedAt: string;
  validUntil: string;
  active: boolean;
}

export interface SkillScorePenaltyMarkerResponse {
  number: number;
  date: string;
}

export interface SkillScoreStatusResponse {
  checkPeriodMonths: number;
  learningGradeSettings?: LearningGradeSettings | null;
  status: SkillScoreStatus;
  activePoints: number;
  nextDeadline: string;
  daysUntilPenalty: number;
  timelineStart: string;
  timelineEnd: string;
  penaltyZoneStart: string;
  penaltyMarkers: SkillScorePenaltyMarkerResponse[];
  history: SkillScoreHistoryItemResponse[];
  penaltyText: string;
  generatedAt: string;
}

export interface AiChatMessage {
  role: AiChatRole;
  content: string;
}

export interface AiChatMessageResponse {
  response: string;
}

export interface SkillTestQuestion {
  id?: string | null;
  questionText: string;
  questionTextTranslation?: TranslationBundle | null;
  answers: string[];
  answerTranslations?: (TranslationBundle | null)[];
  correctAnswerIndex?: number | null;
}

export interface SkillTestGenerateResponse {
  questions: SkillTestQuestion[];
  timeLimitMinutes: number;
}

export interface SkillTestResultResponse {
  submissionId?: string;
  pending?: boolean;
  passed: boolean;
  correctCount: number;
  totalCount: number;
  percentage: number;
}

export interface SkillPracticeTaskResponse {
  id: string;
  status: SkillPracticeTaskStatus;
  taskType: SkillPracticeTaskType;
  owningGroupId?: string | null;
  recipientDepartmentId?: string | null;
  recipientUserId?: string | null;
  recipientPlanfixUserId?: string | null;
  responsibleGroupId?: string | null;
  planfixParentTaskId?: string | null;
  assignedCuratorId: string | null;
  assignedCuratorProfileLink: string | null;
  assignedCurator: SkillAssignedCuratorResponse | null;
  planfixTaskId: number | null;
  planfixTaskUrl: string | null;
  createdAt: string;
  cancelledAt: string | null;
  completedAt: string | null;
  recreateAvailableAt: string | null;
}

export interface SkillWeightSumParams {
  from: string;
  to: string;
}

export interface SkillAiDateRangeParams {
  from: string;
  to: string;
}

export interface SkillAiOperationBreakdownItem {
  operation: SkillAiOperation;
  requestCount: number;
  successCount: number;
  failureCount: number;
  successRate: number;
}

export interface SkillAiModelBreakdownItem {
  provider: string;
  model: string;
  requestCount: number;
  totalTokens: number;
  estimatedCost: number;
}

export interface SkillAiStatisticsSummaryResponse {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  totalTokens: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  estimatedCost: number;
  averageLatencyMs: number;
  successRate: number;
  operationBreakdown: SkillAiOperationBreakdownItem[];
  modelBreakdown: SkillAiModelBreakdownItem[];
}

export interface SkillAiTimeseriesItem {
  date: string;
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  totalTokens: number;
  estimatedCost: number;
}

export interface SkillAiTopUserItem {
  userId: string;
  requestCount: number;
  totalTokens: number;
  estimatedCost: number;
  successRate: number;
}

export interface SkillAiTopSpecializationItem {
  specializationId: string;
  requestCount: number;
  totalTokens: number;
  estimatedCost: number;
  uniqueUsers: number;
}

export interface SkillAiLogItem {
  id: string;
  createdAt: string;
  userId: string;
  specializationId: string;
  operation: SkillAiOperation;
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCost: number;
  success: boolean;
  errorCode: string | null;
  latencyMs: number;
  requestedQuestionsCount: number | null;
  actualQuestionsCount: number | null;
}

export interface SkillAiLogsResponse {
  items: SkillAiLogItem[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

export interface SkillAiLogsQueryParams {
  from?: string;
  to?: string;
  page: number;
  size: number;
  operation?: SkillAiOperation | null;
  success?: boolean | null;
  model?: string;
  userId?: string;
  specializationId?: string;
}

export interface StandardAiQuestionStatisticsRequest {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface SkillLearningPopularityRequest {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface SkillLearningPopularityItemResponse {
  specializationId: string;
  specializationName: string;
  specializationNameTranslation?: TranslationBundle | null;
  groupId: string;
  groupName: string;
  groupNameTranslation?: TranslationBundle | null;
  startedUsers: number;
  level1SelfTestPassedUsers: number;
  level1TestReviewPassedUsers: number;
  level2PassedUsers: number;
  level3PassedUsers: number;
  completedUsers: number;
  completionPercent: number;
}

export interface SkillLearningPopularityPageResponse {
  items: SkillLearningPopularityItemResponse[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

export interface SkillAiBudgetTopUserItem {
  userId: string;
  tokensUsed: number;
  chatMessagesToday: number;
  testsGeneratedToday: number;
  status: string;
}

export interface SkillAiBudgetStatusResponse {
  globalTokensUsed: number;
  globalTokenLimit: number;
  usagePercentage: number;
  status: string;
  testGenerationRequestsToday: number;
  topUsers: SkillAiBudgetTopUserItem[];
}

export interface SkillAiBudgetTokensApiResponse {
  used: number;
  limit: number;
  percent: number;
  status: string;
}

export interface SkillAiBudgetStatusApiResponse {
  globalTokens?: SkillAiBudgetTokensApiResponse;
  globalStatus?: string;
  globalLimit?: number;
  testGenerationRequestsToday?: number;
  topUsers?: SkillAiBudgetTopUserItem[];
}

export const CONTENT_TRANSLATION_COVERAGE_CATEGORY = {
  STANDARDS: 'STANDARDS' as 'STANDARDS',
  LEARNING: 'LEARNING' as 'LEARNING',
  SUGGESTIONS: 'SUGGESTIONS' as 'SUGGESTIONS',
  KARMA: 'KARMA' as 'KARMA',
  GENERAL_NOTIFICATIONS: 'GENERAL_NOTIFICATIONS' as 'GENERAL_NOTIFICATIONS',
};

export type ContentTranslationCoverageCategory = typeof CONTENT_TRANSLATION_COVERAGE_CATEGORY.STANDARDS
  | typeof CONTENT_TRANSLATION_COVERAGE_CATEGORY.LEARNING
  | typeof CONTENT_TRANSLATION_COVERAGE_CATEGORY.SUGGESTIONS
  | typeof CONTENT_TRANSLATION_COVERAGE_CATEGORY.KARMA
  | typeof CONTENT_TRANSLATION_COVERAGE_CATEGORY.GENERAL_NOTIFICATIONS;

export interface ContentTranslationCoverageSectionResponse {
  category: ContentTranslationCoverageCategory;
  total: number;
  ready: number;
  processing: number;
  queued: number;
  retrying: number;
  missing: number;
}

export interface ContentTranslationCoverageResponse {
  generatingNow: boolean;
  calculatedAt: string | null;
  sections: ContentTranslationCoverageSectionResponse[];
}

export interface StandardAiQuestionStatisticsSummaryResponse {
  totalStandards: number;
  trackedStandards: number;
  generatedStandards: number;
  failedStandards: number;
  pendingStandards: number;
  processingStandards: number;
  totalGenerationStates: number;
  activeTrackedStandards: number;
  draftGenerationStates: number;
  orphanedGenerationStates: number;
  pendingGenerations: number;
  processingGenerations: number;
  successfulGenerations: number;
  failedGenerations: number;
  skippedGenerations: number;
  manualOverrideGenerations: number;
  totalGeneratedQuestions: number;
}

export interface StandardAiQuestionStatisticsItem {
  stateId: string;
  standardId: string;
  standardNumber: string;
  standardName: string;
  language: StandardAiQuestionLanguage;
  sourceIdentity: string | null;
  sourceTextHash: string | null;
  status: StandardAiQuestionGenerationStatusName;
  success: boolean;
  generatedQuestionCount: number;
  errorCode: string | null;
  errorMessage: string | null;
  lastTriggerReason: string | null;
  provider: string | null;
  model: string | null;
  attempts: number;
  maxAttempts: number;
  nextAttemptAt: string | null;
  createdAt: string | null;
  generatedAt: string | null;
  updatedAt: string | null;
  activityAt: string | null;
}

export interface StandardAiQuestionStatisticsResponse {
  summary: StandardAiQuestionStatisticsSummaryResponse;
  items: StandardAiQuestionStatisticsItem[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

export interface SkillGroup {
  parentDepartmentId?: string;
  groupType?: string;
  id: string;
  name: string;
  nameTranslation?: TranslationBundle | null;
  defaultDepartmentContext?: boolean;
}

export interface SkillSpecializationGroupOption extends SkillGroup {
  selected: boolean;
  responsibleGroupId: string;
}

export interface SkillAvailableCurator {
  id: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  groupId?: string;
  groupName?: string;
  groupNameTranslation?: TranslationBundle | null;
  localDepartment?: boolean;
  completedLevels?: number;
  maxAvailableAcceptedLevel?: number;
  eligibleForHeadAssignment?: boolean;
}

export interface SkillCuratorOptionView extends SkillAvailableCurator {
  selected: boolean;
  defaultHead: boolean;
  acceptedLevel: number;
  acceptedLevelOptions: number[];
}

export interface SkillCuratorAssignmentResponse {
  userId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  groupId?: string;
  groupName?: string;
  groupNameTranslation?: TranslationBundle | null;
  responsibleGroupId?: string;
  localDepartment?: boolean;
  completedLevels?: number;
  maxAcceptedLevel: number;
  source: string;
  removable: boolean;
  headDefault: boolean;
}

export interface SkillAssignedCuratorResponse {
  id: string;
  fullName: string;
  profileLink: string;
  phone: string | null;
}

export interface SkillDocumentResponse {
  fileId: string;
  fileName: string;
  fileExtension: string;
  size: number;
  contentType: string;
}

export type SkillRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface SkillRequestCreateRequest {
  specializationId: string;
}

export interface SkillRequestResponse {
  id: string;
  userId: string;
  specializationId: string;
  status: SkillRequestStatus;
  createdAt: string;
  resolvedAt: string | null;
}

export function createDefaultSkillLearningGeneralSettings(): SkillLearningGeneralSettingsResponse {
  return {
    testRetakeCooldownHours: 10,
    testGenerationLimitPerUserPerDay: 2,
    maxChatMessageLength: 2000,
    chatMessagesPerUserPerDay: 50,
    chatMessagesPerUserPerMinute: 10,
    learningControlPeriodMonths: 6,
  };
}

export function mergeSkillLearningGeneralSettings(
  settings?: Partial<SkillLearningGeneralSettingsResponse> | null,
): SkillLearningGeneralSettingsResponse {
  return Object.assign({}, createDefaultSkillLearningGeneralSettings(), settings || {});
}

export interface SkillDocumentPreviewLinkResponse {
  token: string;
  fileName: string;
  expiresAt: string;
  previewUrl?: string;
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/models/skill-learning.model.ts